/**
 * BreadcrumbContext
 *
 * Provides a breadcrumb trail for the header bar. Components that expand
 * (clinician rows, measure accordions, facility sub-grids) register
 * themselves via addLevel/removeLevel. The header reads the trail and
 * renders gray pills with X buttons.
 *
 * Synced from keystone-platform/frontend/src/context/BreadcrumbContext.tsx
 * Path adjusted to live alongside other equip contexts in views/equip/context/.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export interface BreadcrumbLevel {
  id: string
  label: string
  depth: number
  onCollapse: () => void
  scrollTarget?: HTMLElement | null
}

interface BreadcrumbContextValue {
  trail: BreadcrumbLevel[]
  addLevel: (level: BreadcrumbLevel) => void
  removeLevel: (id: string) => void
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null)

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [trail, setTrail] = useState<BreadcrumbLevel[]>([])

  const addLevel = useCallback((level: BreadcrumbLevel) => {
    setTrail((prev) => {
      const filtered = prev.filter((l) => l.id !== level.id)
      const next = [...filtered, level].sort((a, b) => a.depth - b.depth)
      // Avoid unnecessary re-render if nothing changed
      if (
        prev.length === next.length &&
        prev.every((l, i) => l.id === next[i].id)
      ) {
        return prev
      }
      return next
    })
  }, [])

  const removeLevel = useCallback((id: string) => {
    setTrail((prev) => {
      if (!prev.some((l) => l.id === id)) return prev
      return prev.filter((l) => l.id !== id)
    })
  }, [])

  const value = useMemo(
    () => ({ trail, addLevel, removeLevel }),
    [trail, addLevel, removeLevel]
  )

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

export function useBreadcrumbs(): BreadcrumbContextValue {
  const ctx = useContext(BreadcrumbContext)
  if (!ctx) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider')
  }
  return ctx
}
